import React, { useCallback, useEffect, useState } from "react";
import CustomDatePicker from "@services/ui-components/src/DatePicker/DatePicker";
import { DateTime } from "luxon";
import { getIn, useFormikContext } from "formik";
import { SellProductForm } from "../../types";
import ZonedInput from "./ZonedInput";
import ZonedTimeInput from "./ZonedTimeInput";

type DatePickerWithTimeProps = {
    name: string;
    placeholder: string;
    value?: string;
    id: string;
    disabled?: boolean;
    timeZone: string;
};

const SUBMIT_FORMAT = "yyyy-MM-dd HH:mm:ss";
const UI_FORMAT = "dd.MM.yyyy HH:mm";

const DatePickerWithTime: React.FC<DatePickerWithTimeProps> = ({
    name,
    value,
    id,
    disabled,
    placeholder,
    timeZone,
    ...rest
}) => {
    const { setFieldValue, errors, touched } = useFormikContext<SellProductForm>();
    const nowDateInZone = DateTime.now().setZone(timeZone);
    const maxDate = nowDateInZone.toJSDate();

    const initialDate = value ? DateTime.fromFormat(value, "yyyy-MM-dd HH:mm:ss").toJSDate() : maxDate;

    const [startDate, setStartDate] = useState<Date | null>(initialDate);

    useEffect(() => {
        if (!value) return;
        const parsed = DateTime.fromFormat(value, SUBMIT_FORMAT, { zone: timeZone });
        if (!parsed.isValid) return;

        const next = parsed.toJSDate();
        setStartDate((prev) => {
            const prevMs = prev?.getTime();
            const nextMs = next.getTime();
            return prevMs !== nextMs ? next : prev;
        });
    }, [value, timeZone]);

    const displayValue = startDate
        ? DateTime.fromJSDate(startDate, { zone: timeZone }).setZone(timeZone).toFormat(UI_FORMAT)
        : "";

    const handleDateChange = useCallback(
        (value: Date | null) => {
            if (value) {
                const formattedDate = DateTime.fromJSDate(value, { zone: timeZone })
                    .setZone(timeZone)
                    .toFormat(SUBMIT_FORMAT);
                setFieldValue(name, formattedDate);
                setStartDate(value);
            } else {
                setFieldValue(name, "");
                setStartDate(null);
            }
        },
        [setFieldValue, name, timeZone]
    );

    const customInput = React.createElement(ZonedInput, {
        value: displayValue,
        placeholder,
        disabled,
        timeZone,
        selectedDate: startDate,
        className: `form-control w-100 datepicker-input text-dark cursor-pointer fs-7 pe-5 ${
            getIn(touched, name) && getIn(errors, name) ? "is-invalid" : ""
        }`,
    });

    return (
        <>
            <div className="position-relative">
                <CustomDatePicker
                    id={id}
                    {...rest}
                    name={name}
                    selected={startDate}
                    showTimeInput
                    dateFormat={UI_FORMAT}
                    timeFormat="HH:mm"
                    className={`form-control w-100 datepicker-input text-dark cursor-pointer fs-7  ${
                        getIn(touched, name) && getIn(errors, name)
                            ? "is-invalid padding-background-position-is-invalid"
                            : ""
                    }`}
                    placeholderText={placeholder}
                    onChange={handleDateChange}
                    maxDate={maxDate}
                    wrapperClassName="w-100 position-relative"
                    showYearDropdown
                    yearDropdownItemNumber={104}
                    scrollableYearDropdown
                    disabled={disabled}
                    customInput={customInput}
                    customTimeInput={
                        <ZonedTimeInput timeZone={timeZone} selectedDate={startDate} disabled={disabled} />
                    }
                />
                <i
                    className="bi bi-calendar-event position-absolute top-50 translate-middle-y end-0"
                    style={{ pointerEvents: "none", paddingRight: "0.844rem" }}
                ></i>
            </div>
        </>
    );
};

export default DatePickerWithTime;
